import React, { useEffect, useMemo, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { useIntl } from '@atlassian/jira-intl';
import TabListUIOld from '@atlassian/jira-navigation-kit-ui-tab-list-old/src/ui/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import type {
	JiraNavigationItemTypeKey,
	ui_navigationKitTabListOld_TabList$key,
} from '@atlassian/jira-relay/src/__generated__/ui_navigationKitTabListOld_TabList.graphql';
import { useIsProjectPagesOptedIn } from '@atlassian/jira-navigation-apps-common/src/controllers/is-project-pages-opted-in/index.tsx';
import { useDroppableTabListOld } from '../controllers/droppable-tab-list/index.tsx';
import JiraTab from './jira-tab/index.tsx';
import messages from './messages.tsx';

type Props = {
	tabsFragment: ui_navigationKitTabListOld_TabList$key;
	addTab?: React.ReactNode;
	typeKeyToRoutesMapping?: Map<JiraNavigationItemTypeKey, Set<string>>;
};

const TabList = ({ tabsFragment, addTab, typeKeyToRoutesMapping }: Props) => {
	const { formatMessage } = useIntl();
	const { group: currentRouteGroup, name: currentRouteName } = useCurrentRoute();
	const tabListRef = useRef<HTMLDivElement>(null);
	const selectedTabRef = useRef<HTMLElement>(null);

	const tabsData = useFragment(
		graphql`
			fragment ui_navigationKitTabListOld_TabList on JiraNavigationItemConnection {
				edges {
					node @required(action: THROW) {
						id @required(action: THROW)
						isDefault @required(action: THROW)
						typeKey
						...jiraTab_navigationKitTabListOld
					}
				}
			}
		`,
		tabsFragment,
	);

	let tabsAll = Array.from(tabsData?.edges || []).filter(Boolean);

	const tabList = useDroppableTabListOld(tabsAll);
	tabsAll = tabList.tabs;

	const { isOptedIn } = useIsProjectPagesOptedIn();
	const tabs = isOptedIn ? tabsAll : tabsAll.filter((edge) => !edge?.node?.id.endsWith('/pages'));

	const defaultViewTypeKey = tabs?.find((edge) => edge?.node?.isDefault)?.node?.typeKey;

	const selectedViewTypeKey = useMemo(
		() =>
			tabs.find(({ node: { typeKey } }) => {
				if (typeKey && typeKeyToRoutesMapping?.has(typeKey)) {
					const routes = typeKeyToRoutesMapping.get(typeKey);
					return routes?.has(currentRouteName) || routes?.has(currentRouteGroup ?? '') || false;
				}
				return false;
			})?.node?.typeKey,
		[currentRouteGroup, currentRouteName, tabs, typeKeyToRoutesMapping],
	);

	useEffect(() => {
		if (!tabListRef.current) {
			return;
		}

		autoScrollForElements({
			element: tabListRef.current,
		});
	}, []);

	// this effect is used to ensure the selected tab is scrolled into view
	// when a new value is detected (e.g. after a mutation or a navigation event)
	useEffect(() => {
		if (selectedViewTypeKey) {
			requestAnimationFrame(() => {
				selectedTabRef.current && selectedTabRef.current.scrollIntoView();
			});
		}
	}, [selectedViewTypeKey]);

	const tabElements = tabs?.map((edge, index) => (
		<JiraTab
			key={edge.node.id}
			id={edge.node.id}
			position={index}
			tabFragment={edge.node}
			beforeItemId={index === 0 ? null : tabs[index - 1]?.node?.id}
			afterItemId={index === tabs.length - 1 ? null : tabs[index + 1]?.node?.id}
			defaultViewTypeKey={defaultViewTypeKey ?? undefined}
			ref={selectedViewTypeKey === edge.node.typeKey ? selectedTabRef : undefined}
		/>
	));

	return (
		<TabListUIOld ref={tabListRef} label={formatMessage(messages.tabGroup)} addTab={addTab}>
			{tabElements}
		</TabListUIOld>
	);
};

export default TabList;
